import {QueryClient} from '@tanstack/react-query';
import {z as r, ZodError} from 'zod';

import {FetchReviewsInfoDataType, getReviewsInfoQueryKey} from '../reviews-info';
import {getReviewNotesQueryKey, reviewNotesSchema, ReviewNoteType} from '../reviews-notes';
import {ReviewDataChildType, ReviewDataTagType, ReviewDataType, ReviewResponsibleUserType} from '../reviews-type';

import {
    PublishReviewUpdateType,
    reviewRealtimeUpdateChangesSchema,
    ReviewRealtimeUpdateChangesType,
} from './review-realtime-presence-type';

const reviewRealtimeUpdateNotesSchema = r.array(reviewNotesSchema);

type ReviewRealtimeFieldNameType = keyof ReviewRealtimeUpdateChangesType & keyof ReviewDataType;

const reviewRealtimeScalarFieldNames: Array<ReviewRealtimeFieldNameType> = [
    'processStatus',
    'sentiment',
    'isHidden',
    'abuseLastDate',
    'deletedAt',
    'moderated',
    'lastChangesDate',
    'hasAccountError',
    'isSynchronizationAccount',
];

const reviewRealtimeBroadcastFieldNames: Array<ReviewRealtimeFieldNameType> = [
    ...reviewRealtimeScalarFieldNames,
    'tags',
    'responsibleUser',
    'children',
    'notes',
];

function getReviewChildKey(child: ReviewDataChildType): number | null {
    if (child.type === 'reply' && child.replyId !== null) {
        return child.replyId;
    }

    if (child.type === 'command' && child.commandId !== null) {
        return child.commandId;
    }

    return null;
}

export function mergeReviewWithPartial(review: ReviewDataType, partial: Partial<ReviewDataType>): ReviewDataType {
    const {children: partialChildren, ...restPartial} = partial;

    if (!partialChildren) {
        return {...review, ...restPartial};
    }

    const existingChildKeys = new Set(
        (review.children || [])
            .map((child) => getReviewChildKey(child))
            .filter((childKey): childKey is number => childKey !== null)
    );
    const allChildrenAreNew = partialChildren.every((child) => {
        const childKey = getReviewChildKey(child);

        return childKey !== null && !existingChildKeys.has(childKey);
    });

    if (allChildrenAreNew) {
        return {
            ...review,
            ...restPartial,
            children: [...(review.children || []), ...partialChildren],
        };
    }

    return {
        ...review,
        ...restPartial,
        children: partialChildren,
    };
}

export function patchReviewNotesInCache(
    queryClient: QueryClient,
    reviewId: number,
    reviewIds: Array<number>,
    notes: Array<ReviewNoteType>
): void {
    queryClient.setQueryData(getReviewNotesQueryKey(reviewId), {
        results: notes,
    });

    if (reviewIds.length === 0) {
        return;
    }

    queryClient.setQueryData(getReviewsInfoQueryKey(reviewIds), (oldData?: FetchReviewsInfoDataType) => {
        if (!oldData) {
            return oldData;
        }

        return {
            ...oldData,
            results: oldData.results.map((info) => (info.id === reviewId ? {...info, notesCount: notes.length} : info)),
        };
    });
}

function isReviewDataChild(candidate: Record<string, unknown>): candidate is ReviewDataChildType {
    return (
        (candidate.type === 'reply' && typeof candidate.replyId === 'number') ||
        (candidate.type === 'command' && typeof candidate.commandId === 'number')
    );
}

function parseReviewDataChildren(children: Array<Record<string, unknown>>): Array<ReviewDataChildType> | null {
    const parsedChildren = children.filter(isReviewDataChild);

    if (parsedChildren.length !== children.length) {
        return null;
    }

    return parsedChildren;
}

function isReviewDataTag(candidate: Record<string, unknown>): candidate is ReviewDataTagType {
    return typeof candidate.id === 'number' && typeof candidate.title === 'string';
}

function parseReviewTags(tags: Array<Record<string, unknown>> | null): ReviewDataType['tags'] | null {
    if (tags === null) {
        return null;
    }

    const parsedTags = tags.filter(isReviewDataTag);

    if (parsedTags.length !== tags.length) {
        return null;
    }

    return parsedTags;
}

function parseResponsibleUser(responsibleUser: Record<string, unknown> | null): ReviewResponsibleUserType | null {
    if (responsibleUser === null) {
        return null;
    }

    if (
        typeof responsibleUser.id !== 'number' ||
        typeof responsibleUser.firstName !== 'string' ||
        typeof responsibleUser.fullName !== 'string' ||
        typeof responsibleUser.email !== 'string'
    ) {
        return null;
    }

    const lastName =
        typeof responsibleUser.lastName === 'string' || responsibleUser.lastName === null
            ? responsibleUser.lastName
            : null;

    return {
        id: responsibleUser.id,
        firstName: responsibleUser.firstName,
        lastName,
        fullName: responsibleUser.fullName,
        email: responsibleUser.email,
    };
}

function mapScalarFields(source: ReviewRealtimeUpdateChangesType): Partial<ReviewDataType> {
    return reviewRealtimeScalarFieldNames.reduce<Partial<ReviewDataType>>((partial, fieldName) => {
        if (!(fieldName in source)) {
            return partial;
        }

        return {
            ...partial,
            [fieldName]: source[fieldName],
        };
    }, {});
}

function mapTagsField(source: ReviewRealtimeUpdateChangesType): Partial<ReviewDataType> {
    if (!('tags' in source)) {
        return {};
    }

    const parsedTags = parseReviewTags(source.tags ?? null);

    if (parsedTags === null) {
        return {};
    }

    return {tags: parsedTags};
}

function mapResponsibleUserField(source: ReviewRealtimeUpdateChangesType): Partial<ReviewDataType> {
    if (!('responsibleUser' in source)) {
        return {};
    }

    if (source.responsibleUser === null) {
        return {responsibleUser: null};
    }

    if (!source.responsibleUser) {
        return {};
    }

    const parsedResponsibleUser = parseResponsibleUser(source.responsibleUser);

    if (!parsedResponsibleUser) {
        return {};
    }

    return {responsibleUser: parsedResponsibleUser};
}

function mapChildrenField(source: ReviewRealtimeUpdateChangesType): Partial<ReviewDataType> {
    if (!('children' in source)) {
        return {};
    }

    const parsedChildren = parseReviewDataChildren(source.children ?? []);

    if (!parsedChildren) {
        return {};
    }

    return {children: parsedChildren};
}

function mapNotesField(source: ReviewRealtimeUpdateChangesType): Partial<ReviewDataType> {
    if (!('notes' in source)) {
        return {};
    }

    const notesParseResult = reviewRealtimeUpdateNotesSchema.safeParse(source.notes);

    if (!notesParseResult.success) {
        return {};
    }

    return {notes: notesParseResult.data};
}

function pickRealtimeBroadcastChanges(partial: Partial<ReviewDataType>): ReviewRealtimeUpdateChangesType {
    return reviewRealtimeBroadcastFieldNames.reduce<ReviewRealtimeUpdateChangesType>((changes, fieldName) => {
        if (!(fieldName in partial)) {
            return changes;
        }

        return {
            ...changes,
            [fieldName]: partial[fieldName],
        };
    }, {});
}

export function mapRealtimeChangesToReviewPartial(changes: ReviewRealtimeUpdateChangesType): Partial<ReviewDataType> {
    return {
        ...mapScalarFields(changes),
        ...mapTagsField(changes),
        ...mapResponsibleUserField(changes),
        ...mapChildrenField(changes),
        ...mapNotesField(changes),
    };
}

export function logReviewPresenceParseFailure(scope: string, error: ZodError): void {
    if (typeof IS_PRODUCTION === 'boolean' && IS_PRODUCTION) {
        return;
    }

    // eslint-disable-next-line no-restricted-globals
    console.warn(`[review-presence] ${scope} parse failed`, error.flatten());
}

export function getValidatedBroadcastChanges(
    patchData: Partial<ReviewDataType>
): ReviewRealtimeUpdateChangesType | null {
    const parseResult = reviewRealtimeUpdateChangesSchema.safeParse(pickRealtimeBroadcastChanges(patchData));

    if (!parseResult.success) {
        logReviewPresenceParseFailure('broadcast-from-patch', parseResult.error);

        return null;
    }

    return parseResult.data;
}

type ApplyReviewUpdateWithBroadcastOptionsType = {
    reviewId: number;
    partialReviewData: Partial<ReviewDataType>;
    patchReviewInCache: (reviewId: number, partialReviewData: Partial<ReviewDataType>) => void;
    findBrandIdForReview: (reviewId: number) => number | undefined;
    publishReviewUpdate: PublishReviewUpdateType;
};

export function applyReviewUpdateWithBroadcast(options: ApplyReviewUpdateWithBroadcastOptionsType): void {
    const {reviewId, partialReviewData, patchReviewInCache, findBrandIdForReview, publishReviewUpdate} = options;
    const changesParseResult = reviewRealtimeUpdateChangesSchema.safeParse(partialReviewData);

    if (!changesParseResult.success) {
        logReviewPresenceParseFailure('broadcast', changesParseResult.error);
        patchReviewInCache(reviewId, partialReviewData);

        return;
    }

    const patchData = mapRealtimeChangesToReviewPartial(changesParseResult.data);

    patchReviewInCache(reviewId, patchData);

    const broadcastChanges = getValidatedBroadcastChanges(patchData);

    if (!broadcastChanges) {
        return;
    }

    const brandId = findBrandIdForReview(reviewId);

    if (!brandId) {
        return;
    }

    publishReviewUpdate(reviewId, brandId, broadcastChanges);
}
